import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry } from 'ag-grid-community';
import type {
  CellClassParams,
  CellValueChangedEvent,
  ColDef,
  ColGroupDef,
  Column,
  GetMainMenuItems,
  GridOptions,
  ICellRendererParams,
  IRowNode,
  RowClassParams,
  FillEndEvent,
} from 'ag-grid-community';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

import './BombiixAGGrid.globals.scss';
import './BombiixAGGrid.scss';
import TextInputCell from './cells/TextInputCell';
import RowDragCell from './cells/RowDragCell';
import CurrencyInputCell from './cells/CurrencyInputCell';
import PercentInputCell from './cells/PercentInputCell';
import SelectInputCell from './cells/SelectInputCell';
import CheckboxCell from './cells/CheckboxCell';
import RadioCell from './cells/RadioCell';
import RadioGroupCell from './cells/RadioGroupCell';
import DateInputCell from './cells/DateInputCell';
import ReadonlyTextCell from './cells/ReadonlyTextCell';
import TotalsFooterCell from './cells/TotalsFooterCell';
import RowActionsCell from './cells/RowActionsCell';
import ExpandChevronCell from './cells/ExpandChevronCell';
import SortableHeader from './headers/SortableHeader';
import HeaderStatChip, { isBlankStat } from './headers/HeaderStatChip';
import ExpandAllHeader from './headers/ExpandAllHeader';
import FloatingBottomBar from './FloatingBottomBar';
import { collectLeafColumnDefs, findNextNavigableColId, findNextNavigableRowIndex } from './activeCellNavigation';
import Loader from '../Utility/Loader/Loader';
import type {
  BombiixAGGridProps,
  BombiixCellType,
  BombiixColumnDef,
  BombiixColumnDefOrGroup,
  BombiixColumnGroupDef,
  BombiixFillBatchItem,
  BombiixRow,
  BombiixRowId,
  FloatingAction,
  HeaderStat,
  OnRowChange,
} from './types';
import { getTruncatedCellTooltip, GRID_TOOLTIP_SHOW_DELAY_MS } from '../../utils/grid-cell-tooltip';
import { getClipboardContextMenuItems } from '../../utils/grid-context-menu';
import { clipboardTextOf, clipboardValueOf, copyFillValue, rowsInRange, startsCellEditor } from './fillClipboard';

ModuleRegistry.registerModules([AllEnterpriseModule]);

const ENV_LICENSE = process.env.REACT_APP_AG_GRID_LICENSE;
if (ENV_LICENSE) LicenseManager.setLicenseKey(ENV_LICENSE);

const CELL_RENDERERS: Record<BombiixCellType, React.ComponentType<ICellRendererParams<any>>> = {
  text: TextInputCell as React.ComponentType<ICellRendererParams<any>>,
  currency: CurrencyInputCell as React.ComponentType<ICellRendererParams<any>>,
  percent: PercentInputCell as React.ComponentType<ICellRendererParams<any>>,
  select: SelectInputCell as React.ComponentType<ICellRendererParams<any>>,
  checkbox: CheckboxCell as React.ComponentType<ICellRendererParams<any>>,
  radio: RadioCell as React.ComponentType<ICellRendererParams<any>>,
  radioGroup: RadioGroupCell as React.ComponentType<ICellRendererParams<any>>,
  date: DateInputCell as React.ComponentType<ICellRendererParams<any>>,
  readonlyText: ReadonlyTextCell as React.ComponentType<ICellRendererParams<any>>,
  totalsFooter: TotalsFooterCell as React.ComponentType<ICellRendererParams<any>>,
};

const HEADER_HEIGHT = 40;

const isFooterRow = (data: unknown): boolean => Boolean(data && (data as { __cagFooter?: boolean }).__cagFooter);

/* Renders nothing — used to blank a cell whose column opts out via getCellHidden. */
const EmptyCell: React.ComponentType = () => null;

/* Stable empty-object identity for the gridOptions default. */
const EMPTY_GRID_OPTIONS: GridOptions = {};

/* Stable empty-array identity for the floatingActions default. */
const EMPTY_FLOATING_ACTIONS: FloatingAction[] = [];

/* Grid default row height, and how close to the end of the rows a scroll must
   land before the next page is requested. */
const DEFAULT_ROW_HEIGHT = 40;
const LOAD_MORE_THRESHOLD_PX = 200;

/* Cell types the fill handle can write to. A radio group stores its row's own option id, so it fills
   like a select; the single-row `radio` picker, readonly and totals cells are excluded. */
const FILL_ELIGIBLE_CELL_TYPES: ReadonlySet<BombiixCellType> = new Set<BombiixCellType>(['text', 'currency', 'percent', 'select', 'checkbox', 'date', 'radioGroup']);

const TAB_TARGET_SELECTOR = 'input:not([type="hidden"]):not([disabled]):not([readonly]), select:not([disabled]), textarea:not([disabled]):not([readonly])';

/* The text/number cell inputs, and only those: `.cag-cell-input` is TextInputCell and
   HierActionInputCell, `.cag-affix-input` is CurrencyInputCell and PercentInputCell. Named
   positively rather than as an exclusion list so Enter can never reach a control that needs it —
   SelectInputCell's react-select input carries neither class, and DateInputCell's NewDatePicker
   renders a button trigger, not an input. A new cell opts in by wearing one of these classes. */
const COMMIT_ON_ENTER_SELECTOR = 'input.cag-cell-input, input.cag-affix-input';

/* The chip's drawn geometry, mirroring `$stat-chip-inset` and `.cag-header-stat`'s width in
   BombiixAGGrid.scss. Needed in JS because the band sits outside the grid root and its chips are
   positioned by measurement, so whether one fits between the pinned blocks cannot be left to CSS.
   Keep in step with the stylesheet — a chip measured wider than it is drawn merely hides early. */
const STAT_CHIP_WIDTH = 94;
const STAT_CHIP_INSET = 10;

/**
 * Commits the cell an Enter was pressed in, by blurring its input.
 *
 * The blur runs the cell's own `onBlur`, which lands the final keystroke through
 * `useDeferredRowChange`'s flush, and emits the `focusout` a host page can listen for. Whether the
 * commit also *saves* is the host's decision, not the cell's: the Costing TODO grid wires
 * `focusout` to its calculate request, every other consumer simply unfocuses.
 *
 * `preventDefault` additionally suppresses the implicit form submit where a grid sits in a `form`.
 *
 * @returns true when the key was handled, so the caller stops.
 */
function commitCellOnEnter(event: globalThis.KeyboardEvent): boolean {
  if (event.key !== 'Enter' || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return false;
  const target = event.target as HTMLElement | null;
  if (!target?.matches?.(COMMIT_ON_ENTER_SELECTOR)) return false;
  event.preventDefault();
  (target as HTMLInputElement).blur();
  return true;
}

/**
 * Re-enters editing from the active (highlighted) cell, for grids with active-cell navigation on.
 *
 * Enter puts the caret back at the end of the cell's input; a printable character focuses the
 * input with its text selected, so the browser's own insertion replaces the value (Excel
 * type-to-overwrite). Only reacts when the key landed on the cell element itself — keys pressed
 * inside an input belong to the input. `preventDefault` is for Enter only: calling it on a
 * printable key would suppress the very insertion the select-all relies on.
 *
 * @returns true when the key was handled, so the caller stops.
 */
function reenterCellOnKey(event: globalThis.KeyboardEvent): boolean {
  const target = event.target as HTMLElement | null;
  if (!target?.classList?.contains('ag-cell')) return false;
  if (event.altKey || event.ctrlKey || event.metaKey) return false;
  const isEnter = event.key === 'Enter';
  const isPrintable = event.key.length === 1;
  if (!isEnter && !isPrintable) return false;
  const control = target.querySelector<HTMLInputElement>(TAB_TARGET_SELECTOR);
  if (!control) return false;
  if (isEnter) event.preventDefault();
  control.focus();
  if (isPrintable && typeof control.select === 'function') {
    control.select();
  } else if (typeof control.setSelectionRange === 'function') {
    try {
      const caret = control.value != null ? String(control.value).length : 0;
      control.setSelectionRange(caret, caret);
    } catch {
      /* number/email inputs reject setSelectionRange — focus alone is fine. */
    }
  }
  return true;
}

let headerMeasureCtx: CanvasRenderingContext2D | null = null;

/**
 * Font the header label actually renders in. Read from a live header cell so the measurement
 * tracks the stylesheet; the literal is only the pre-first-paint fallback and must stay in step
 * with `.cag-header-label` in BombiixAGGrid.scss.
 */
const HEADER_FONT_FALLBACK = '14px "Aktiv Grotesk Medium", Arial, sans-serif';
const measuredHeaderFont = (): string => {
  const label = document.querySelector('.cag-header-label');
  if (!label) return HEADER_FONT_FALLBACK;
  return window.getComputedStyle(label).font || HEADER_FONT_FALLBACK;
};

/**
 * Canvas measures an unloaded webfont with the fallback face's metrics, which are narrower than
 * Aktiv Grotesk — the label then paints wider than the column it was sized for and clips. Columns
 * are built before the face is guaranteed ready, so pad proportionally until it is, and drop the
 * cached context once it loads so later grids measure exactly.
 */
const UNLOADED_FONT_SAFETY = 1.15;
if (typeof document !== 'undefined' && document.fonts) {
  document.fonts.ready.then(() => {
    headerMeasureCtx = null;
  });
}

const headerFitWidth = (label: string | undefined, required: boolean | undefined, minWidth: number | undefined): number => {
  const floor = minWidth || 120;
  if (!label || typeof document === 'undefined') return floor;
  if (!headerMeasureCtx) {
    headerMeasureCtx = document.createElement('canvas').getContext('2d');
    if (headerMeasureCtx) headerMeasureCtx.font = measuredHeaderFont();
  }
  if (!headerMeasureCtx) return floor;
  const HORIZONTAL_PADDING = 24;
  const SORT_ICON_ROOM = 18;
  const REQUIRED_ASTERISK = required ? 12 : 0;
  /* Sub-pixel rounding and letter-spacing leave the canvas a couple of px short of what the
     browser paints, so keep a small flat margin on top of the webfont factor. */
  const ROUNDING_MARGIN = 4;
  const fontReady = Boolean(document.fonts?.check?.(headerMeasureCtx.font));
  const text = Math.ceil(headerMeasureCtx.measureText(label).width) * (fontReady ? 1 : UNLOADED_FONT_SAFETY);
  const measured = Math.ceil(text) + HORIZONTAL_PADDING + SORT_ICON_ROOM + REQUIRED_ASTERISK + ROUNDING_MARGIN;
  return Math.max(measured, floor);
};

/** One measured chip in the band above the grid: which column it belongs to and where it sits. */
interface StatChipPlacement {
  colId: string;
  left: number;
  stat: HeaderStat;
}

interface HandlersRef<TRow extends BombiixRow> {
  onRowChange: OnRowChange | undefined;
  resolveRowId: (row: TRow | undefined) => BombiixRowId | undefined;
  onAddRow: ((row?: TRow) => void) | undefined;
  onDeleteRow: ((rowId: BombiixRowId) => void) | undefined;
  canAddRow: ((row: TRow) => boolean) | undefined;
  canDeleteRow: ((row: TRow) => boolean) | undefined;
  isRowReadOnly: ((row: TRow) => boolean) | undefined;
  onFillCells: ((batch: BombiixFillBatchItem<TRow>[], sourceRows: TRow[]) => void) | undefined;
  renderRowActionExtras: ((params: ICellRendererParams<TRow> & { rowReadOnly: boolean }) => React.ReactNode) | undefined;
}

/**
 * Generic inline-editable grid built on ag-grid-react. See BombiixColumnDef for
 * the per-column contract (cellType, cellProps, footerCellProps, getCellError,
 * fillable, etc.). Uses PIM design-system controls (NewInput, NewCheckbox,
 * NewRadio, NewSelectMultiSelect) inside standard cells; HierActionInputCell
 * keeps a native input to accommodate its inline add/delete row layout.
 */
function BombiixAGGrid<TRow extends BombiixRow = BombiixRow>({
  columnDefs = [],
  rowData = [],
  refreshChangedRowsOnly = false,
  getRowId,
  onRowChange,
  onAddRow,
  onDeleteRow,
  readOnly = false,
  isRowReadOnly,
  canAddRow,
  canDeleteRow,
  getRowClass,
  pinnedBottomRowData,
  currencySymbol = '£',
  detailRowRenderer,
  isRowMaster,
  treeData = false,
  getDataPath,
  autoGroupColumnDef,
  groupDefaultExpanded = 0,
  radioGroupName,
  enableRowDrag = false,
  canRowDrag,
  onRowReorder,
  rowDragTitle,
  selectedRadioRowId,
  height,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  autoSizeColumns = false,
  revealHeaderControlsOnHover = true,
  enableFillHandle = false,
  fillHandleDirection = 'y',
  onFillCells,
  suppressTabNavigation = false,
  enableActiveCellNavigation = false,
  sortable = true,
  showColumnChooser = false,
  showColumnMenu = true,
  suppressColumnResize = false,
  fullWidthHorizontalScroll = false,
  fullWidthHorizontalScrollEdgeRight = false,
  hideCellErrorMessage = false,
  renderRowActionExtras,
  rowActionsWidth,
  loading = false,
  floatingActions = EMPTY_FLOATING_ACTIONS,
  headerStats,
  rowActionsHeader,
  rowActionsClassName,
  gridOptions = EMPTY_GRID_OPTIONS as GridOptions<TRow>,
  enableClipboardContextMenu = false,
  enableCellCopy = false,
}: BombiixAGGridProps<TRow>) {
  const gridRef = useRef<AgGridReact<TRow>>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const fillBatchRef = useRef<BombiixFillBatchItem<TRow>[]>([]);
  const fillFlushScheduledRef = useRef<boolean>(false);
  const fillSourceRowsRef = useRef<TRow[]>([]);
  /* Last horizontal position the USER scrolled to. Recorded from real scroll events so it survives
     the container-width churn described in applyFullWidthHScroll, which clamps the live value. */
  const lastHScrollLeftRef = useRef<number>(0);

  const resolveRowId = useCallback(
    (row: TRow | undefined): BombiixRowId | undefined => (getRowId ? getRowId(row as TRow) : (row as { id?: BombiixRowId } | undefined)?.id),
    [getRowId],
  );

  const handlersRef = useRef<HandlersRef<TRow>>({
    onRowChange: undefined,
    resolveRowId,
    onAddRow: undefined,
    onDeleteRow: undefined,
    canAddRow: undefined,
    canDeleteRow: undefined,
    isRowReadOnly: undefined,
    onFillCells: undefined,
    renderRowActionExtras: undefined,
  });
  handlersRef.current = {
    onRowChange,
    resolveRowId,
    onAddRow,
    onDeleteRow,
    canAddRow,
    canDeleteRow,
    isRowReadOnly,
    onFillCells,
    renderRowActionExtras,
  };

  /* Read through a ref so the active-cell navigation handlers merged into gridOptions stay
     correct without rebuilding mergedGridOptions on every columnDefs or readOnly change. */
  const activeCellNavRef = useRef<{ leafDefs: Map<string, BombiixColumnDef<TRow>>; gridReadOnly: boolean }>({ leafDefs: new Map(), gridReadOnly: false });
  activeCellNavRef.current = {
    leafDefs: enableActiveCellNavigation || enableFillHandle || enableCellCopy ? collectLeafColumnDefs(columnDefs) : activeCellNavRef.current.leafDefs,
    gridReadOnly: readOnly,
  };

  const stableOnRowChange = useCallback<OnRowChange>((rowId, field, value) => handlersRef.current.onRowChange?.(rowId, field, value), []);
  const stableResolveRowId = useCallback((row: TRow | undefined) => handlersRef.current.resolveRowId(row), []);
  const stableOnAddRow = useCallback((row?: TRow) => handlersRef.current.onAddRow?.(row), []);
  const stableOnDeleteRow = useCallback((rowId: BombiixRowId) => handlersRef.current.onDeleteRow?.(rowId), []);
  const stableCanAddRow = useCallback((row: TRow) => (handlersRef.current.canAddRow ? handlersRef.current.canAddRow(row) : true), []);
  const stableCanDeleteRow = useCallback((row: TRow) => (handlersRef.current.canDeleteRow ? handlersRef.current.canDeleteRow(row) : true), []);
  const stableIsRowReadOnly = useCallback((row: TRow) => (handlersRef.current.isRowReadOnly ? handlersRef.current.isRowReadOnly(row) : false), []);

  /*
   * `renderRowActionExtras` counts too. A grid whose row actions are ALL extras —
   * edit, mail, an overflow menu, a confirm/discard pair on the row being edited —
   * passes neither `onAddRow` nor `onDeleteRow`, and was silently given no actions
   * column at all: the renderer ran for nothing because the column that hosts it
   * was never built.
   */
  const hasRowActions = Boolean(onAddRow || onDeleteRow || renderRowActionExtras);

  const builtColumnDefs = useMemo<(ColDef<TRow> | ColGroupDef<TRow>)[]>(() => {
    const cols: (ColDef<TRow> | ColGroupDef<TRow>)[] = [];

    const structuralColProps: Partial<ColDef<TRow>> = {
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
    };

    if (enableRowDrag) {
      cols.push({
        colId: '__cag_drag',
        headerName: '',
        width: 36,
        minWidth: 36,
        maxWidth: 36,
        cellRenderer: RowDragCell,
        cellRendererParams: { canRowDrag, dragTitle: rowDragTitle },
        pinned: 'left',
        lockPinned: true,
        ...structuralColProps,
      });
    }

    if (detailRowRenderer && !treeData) {
      cols.push({
        colId: '__cag_expand',
        headerName: '',
        headerComponent: ExpandAllHeader,
        width: 40,
        minWidth: 40,
        maxWidth: 40,
        cellRenderer: ExpandChevronCell,
        pinned: 'left',
        ...structuralColProps,
      });
    }

    const transformLeaf = (col: BombiixColumnDef<TRow>): ColDef<TRow> => {
      const {
        cellType,
        cellRenderer,
        cellProps = {},
        footerCellProps,
        sortable: columnSortable = sortable,
        getCellDisabled,
        getCellReadOnly,
        getCellError,
        getCellClass,
        getCellHidden,
        getCellProps,
        fillable,
        required: _required,
        dppRequired: _dppRequired,
        getClipboardText: _getClipboardText,
        ...rest
      } = col;

      const baseRenderer = cellRenderer || (cellType ? CELL_RENDERERS[cellType] : undefined);
      const isInteractive = cellType && cellType !== 'readonlyText' && cellType !== 'totalsFooter';

      const isFillEligible = enableFillHandle && fillable !== false && (fillable === true || (cellType && FILL_ELIGIBLE_CELL_TYPES.has(cellType)));
      const fillEditable = (params: { data?: TRow | null | undefined } | null | undefined) => {
        const row = params?.data;
        if (isFooterRow(row)) return false;
        if (!row) return false;
        if (getCellHidden && getCellHidden(row)) return false;
        const rowReadOnly = readOnly || stableIsRowReadOnly(row);
        const cellReadOnly = getCellReadOnly ? getCellReadOnly(row) : false;
        const cellDisabled = getCellDisabled ? getCellDisabled(row) : false;
        return !rowReadOnly && !cellReadOnly && !cellDisabled;
      };

      const cellRendererSelector = baseRenderer
        ? (params: ICellRendererParams<TRow>) => {
            const row = params?.data;
            if (isFooterRow(row)) {
              if (footerCellProps || cellType === 'totalsFooter') {
                return { component: TotalsFooterCell };
              }
              return { component: undefined };
            }
            if (!row) return { component: baseRenderer };
            if (getCellHidden && getCellHidden(row)) {
              return { component: EmptyCell };
            }
            const rowReadOnly = readOnly || stableIsRowReadOnly(row);
            const cellReadOnly = rowReadOnly || (getCellReadOnly ? getCellReadOnly(row) : false);
            if (cellReadOnly && isInteractive) {
              return { component: ReadonlyTextCell };
            }
            return { component: baseRenderer };
          }
        : undefined;

      /* The column's own cellRendererParams live on `rest` and would be replaced wholesale by the
      wrapper's function below, silently dropping anything the caller passed to its renderer. */
      const columnRendererParams = (rest as ColDef<TRow>).cellRendererParams;
      const resolveColumnRendererParams = (params: ICellRendererParams<TRow>) =>
        typeof columnRendererParams === 'function' ? columnRendererParams(params) : columnRendererParams;

      const cellRendererParams = baseRenderer
        ? (params: ICellRendererParams<TRow>) => {
            const row = params?.data;
            if (isFooterRow(row)) {
              return { ...resolveColumnRendererParams(params), ...cellProps, ...(footerCellProps || {}) };
            }
            const rowReadOnly = row ? readOnly || stableIsRowReadOnly(row) : readOnly;
            return {
              onRowChange: stableOnRowChange,
              getRowId: stableResolveRowId,
              currencySymbol,
              groupName: radioGroupName,
              selectedRowId: selectedRadioRowId,
              ...cellProps,
              ...(row && getCellProps ? getCellProps(row) : {}),
              ...resolveColumnRendererParams(params),
              disabled: rowReadOnly || (row && getCellDisabled ? getCellDisabled(row) : (cellProps as { disabled?: boolean }).disabled),
              error: row && getCellError ? getCellError(row) : undefined,
            };
          }
        : undefined;

      const composedCellClass = (params: CellClassParams<TRow>): string | string[] => {
        if (isFooterRow(params?.data)) return 'cag-cell-class-footer';
        const fromHook = params?.data && getCellClass ? getCellClass(params.data) : undefined;
        const rawColDefCellClass = (rest as ColDef<TRow>).cellClass;
        const fromColDef = typeof rawColDefCellClass === 'function' ? rawColDefCellClass(params) : rawColDefCellClass;
        const classes: string[] = [fromHook, fromColDef].flat().filter((c): c is string => Boolean(c));
        if (enableFillHandle && !(isFillEligible && fillEditable(params))) classes.push('cag-cell-nofill');
        return classes;
      };

      const nextCol: ColDef<TRow> = {
        minWidth: 120,
        resizable: !suppressColumnResize,
        sortable: columnSortable,
        suppressHeaderMenuButton: !showColumnMenu,
        ...(rest as ColDef<TRow>),
        cellClass: composedCellClass,
      };

      /* Forward the column's `required` / `dppRequired` flags to the header so SortableHeader can
      render the mandatory asterisk. Both are destructured out of the colDef above (AG Grid doesn't
      know them), so without this the header never sees them and the "*" never appears.

*/
      if (_required || _dppRequired) {
        nextCol.headerComponentParams = {
          ...(rest.headerComponentParams as Record<string, unknown> | undefined),
          required: _required,
          dppRequired: _dppRequired,
        };
      }

      if (cellRendererSelector) {
        nextCol.cellRendererSelector = cellRendererSelector;
        delete nextCol.cellRenderer;
      } else if (baseRenderer) {
        nextCol.cellRenderer = baseRenderer;
      }

      if (cellRendererParams) {
        nextCol.cellRendererParams = cellRendererParams;
      }

      if (autoSizeColumns && rest.headerName) {
        const headerFit = headerFitWidth(rest.headerName, col.required, rest.minWidth);
        if (rest.flex) {
          nextCol.minWidth = Math.max(rest.minWidth || 0, headerFit);
        } else {
          nextCol.width = headerFit;
          delete nextCol.flex;
        }
      }

      if (isFillEligible) {
        nextCol.editable = fillEditable;
      }

      return nextCol;
    };

    columnDefs.forEach((col) => {
      if (col && 'children' in col && Array.isArray((col as BombiixColumnGroupDef<TRow>).children)) {
        const { children, ...groupRest } = col as BombiixColumnGroupDef<TRow>;
        cols.push({ ...(groupRest as ColGroupDef<TRow>), children: children.map(transformLeaf) });
      } else {
        cols.push(transformLeaf(col as BombiixColumnDef<TRow>));
      }
    });

    if (hasRowActions) {
      const actionsWidth = rowActionsWidth || 80;
      cols.push({
        colId: '__cag_actions',
        headerName: '',
        ...(rowActionsHeader ? { headerComponent: () => <>{rowActionsHeader}</> } : {}),
        width: actionsWidth,
        minWidth: actionsWidth,
        maxWidth: Math.max(actionsWidth, 100),
        pinned: 'right',
        ...(rowActionsClassName || enableFillHandle
          ? { cellClass: [rowActionsClassName, enableFillHandle ? 'cag-cell-nofill' : undefined].filter((name): name is string => Boolean(name)) }
          : {}),
        ...(enableFillHandle ? { suppressFillHandle: true, suppressPaste: true } : {}),
        cellRenderer: RowActionsCell,
        cellRendererParams: (params: ICellRendererParams<TRow>) => {
          const row = params?.data;
          if (isFooterRow(row)) return {};
          const rowReadOnly = row ? readOnly || stableIsRowReadOnly(row) : readOnly;
          const extras = handlersRef.current.renderRowActionExtras ? handlersRef.current.renderRowActionExtras({ ...params, rowReadOnly }) : null;
          return {
            onAddRow: handlersRef.current.onAddRow ? stableOnAddRow : undefined,
            onDeleteRow: handlersRef.current.onDeleteRow ? stableOnDeleteRow : undefined,
            getRowId: stableResolveRowId,
            canAdd: rowReadOnly || !row ? false : stableCanAddRow(row),
            canDelete: rowReadOnly || !row ? false : stableCanDeleteRow(row),
            extras,
          };
        },
        ...structuralColProps,
      });
    }

    return cols;
  }, [
    columnDefs,
    detailRowRenderer,
    hasRowActions,
    stableOnRowChange,
    stableResolveRowId,
    stableOnAddRow,
    stableOnDeleteRow,
    stableCanAddRow,
    stableCanDeleteRow,
    stableIsRowReadOnly,
    currencySymbol,
    radioGroupName,
    selectedRadioRowId,
    readOnly,
    treeData,
    autoSizeColumns,
    enableFillHandle,
    sortable,
    showColumnMenu,
    suppressColumnResize,
    rowActionsWidth,
    rowActionsHeader,
    rowActionsClassName,
  ]);

  const resolvedAutoGroupColumnDef = useMemo<ColDef<TRow> | undefined>(() => {
    if (!treeData) return undefined;
    return {
      headerName: '',
      headerComponent: ExpandAllHeader,
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      pinned: 'left',
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      cellRenderer: ExpandChevronCell,
      ...(autoGroupColumnDef || {}),
    };
  }, [treeData, autoGroupColumnDef]);

  const defaultColDef = useMemo<ColDef<TRow>>(
    () => ({
      suppressMovable: false,
      headerComponent: SortableHeader,
      tooltipValueGetter: getTruncatedCellTooltip,
      suppressKeyboardEvent: (params) => {
        const tag = (params?.event?.target as HTMLElement | undefined)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
        return (enableActiveCellNavigation || enableFillHandle) && startsCellEditor(params.event);
      },
    }),
    [enableActiveCellNavigation, enableFillHandle],
  );

  /**
   * Moves AG Grid's cell focus to the cell containing `target`, making it the active
   * (highlighted) cell after an Enter commit. No-op when the target is not inside a cell.
   */
  const focusCellOf = useCallback((target: HTMLElement | null) => {
    const api = gridRef.current?.api;
    if (!api || api.isDestroyed?.()) return;
    const cellEl = target?.closest?.('.ag-cell');
    const rowEl = cellEl?.closest?.('.ag-row');
    const colId = cellEl?.getAttribute('col-id');
    const rowIndexAttr = rowEl?.getAttribute('row-index');
    if (!colId || rowIndexAttr == null) return;
    api.setFocusedCell(Number(rowIndexAttr), colId);
  }, []);

  /**
   * Keyboard behaviour the grid owns itself, because AG Grid never sees these events:
   * `defaultColDef.suppressKeyboardEvent` drops every key pressed inside an INPUT.
   *
   * Enter commits the cell it was pressed in — and, with active-cell navigation on, leaves that cell highlighted so
   * the arrow keys can take over. Tab moves between the grid's controls in DOM order instead of
   * AG Grid's cell-by-cell walk.
   */
  const handleGridKeyDown = useCallback(
    (event: globalThis.KeyboardEvent) => {
      if (commitCellOnEnter(event)) {
        if (enableActiveCellNavigation) focusCellOf(event.target as HTMLElement | null);
        return;
      }
      if (enableActiveCellNavigation && reenterCellOnKey(event)) return;

      const target = event.target as HTMLElement | null;
      if (suppressTabNavigation || event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) return;
      if (!target?.matches?.(TAB_TARGET_SELECTOR)) return;

      const currentTarget = event.currentTarget as HTMLElement | null;
      if (!currentTarget) return;
      const controls = Array.from(currentTarget.querySelectorAll<HTMLElement>(TAB_TARGET_SELECTOR)).filter((el) => el === target || el.offsetParent !== null);
      const currentIndex = controls.indexOf(target);
      if (currentIndex === -1) return;

      const nextIndex = currentIndex + (event.shiftKey ? -1 : 1);
      if (nextIndex < 0 || nextIndex >= controls.length) return;

      event.preventDefault();
      const next = controls[nextIndex] as HTMLInputElement;
      next.focus();
      const caret = next.value != null ? String(next.value).length : 0;
      if (typeof next.setSelectionRange === 'function') {
        try {
          next.setSelectionRange(caret, caret);
        } catch {
          /* number/email inputs reject setSelectionRange — focus alone is fine. */
        }
      }
    },
    [suppressTabNavigation, enableActiveCellNavigation, focusCellOf],
  );

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return undefined;
    const listener = (e: Event) => handleGridKeyDown(e as unknown as globalThis.KeyboardEvent);
    wrapper.addEventListener('keydown', listener);
    return () => wrapper.removeEventListener('keydown', listener);
  }, [handleGridKeyDown]);

  const composedRowClass = useCallback(
    (params: RowClassParams<TRow>): string | string[] => {
      if (isFooterRow(params?.data)) return 'cag-row-footer';
      return getRowClass ? getRowClass(params?.data) ?? '' : '';
    },
    [getRowClass],
  );

  const handleFillValueChange = useCallback(
    (event: CellValueChangedEvent<TRow>) => {
      if (event?.newValue === event?.oldValue) return;
      const field = event?.colDef?.field;
      if (!field || !event.data) return;
      const rowId = stableResolveRowId(event.data);
      if (rowId === undefined) return;
      const fillCells = handlersRef.current.onFillCells;
      if (fillCells) {
        fillBatchRef.current.push({ rowId, field, oldValue: event.oldValue, newValue: event.newValue, data: event.data });
        if (!fillFlushScheduledRef.current) {
          fillFlushScheduledRef.current = true;
          Promise.resolve().then(() => {
            const batch = fillBatchRef.current;
            const sourceRows = fillSourceRowsRef.current;
            fillBatchRef.current = [];
            fillSourceRowsRef.current = [];
            fillFlushScheduledRef.current = false;
            if (batch.length) handlersRef.current.onFillCells?.(batch, sourceRows);
          });
        }
        return;
      }
      stableOnRowChange(rowId, field, event.newValue);
    },
    [stableOnRowChange, stableResolveRowId],
  );

  /**
   * Keeps the rows a fill was dragged from for the pending `onFillCells` batch. fillEnd fires after
   * every cell write and before the batch flushes; a fill that changed nothing has no batch, so its
   * source is dropped at once rather than leaking into a later paste.
   */
  const recordFillSource = useCallback((event: FillEndEvent<TRow>) => {
    fillSourceRowsRef.current = fillFlushScheduledRef.current ? rowsInRange(event.api, event.initialRange) : [];
  }, []);

  const getGridRowId = useCallback((params: { data: TRow }) => String(stableResolveRowId(params.data)), [stableResolveRowId]);
  const previousRowDataRef = useRef<Map<string, TRow>>(new Map());

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api || api.isDestroyed?.()) return;
    const active = typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null;
    const activeTag = active?.tagName;
    const isEditing = activeTag === 'INPUT' || activeTag === 'SELECT' || activeTag === 'TEXTAREA';
    const focusInThisGrid = isEditing && wrapperRef.current?.contains(active);
    const focusedColId = focusInThisGrid ? active?.closest?.('.ag-cell')?.getAttribute('col-id') : undefined;
    const focusedRowIndex = Number(active?.closest?.('.ag-row')?.getAttribute('row-index'));
    const otherColumnIds = () => (api.getColumns?.() || []).map((column: Column) => column.getColId()).filter((colId: string) => colId !== focusedColId);
    if (refreshChangedRowsOnly) {
      const previous = previousRowDataRef.current;
      previousRowDataRef.current = new Map(rowData.map((row) => [String(stableResolveRowId(row)), row]));
      const changedNodes = rowData
        .filter((row) => previous.get(String(stableResolveRowId(row))) !== row)
        .map((row) => api.getRowNode(String(stableResolveRowId(row))))
        .filter((node): node is IRowNode<TRow> => Boolean(node));
      const typingNode = focusedColId ? changedNodes.find((node) => node.rowIndex === focusedRowIndex) : undefined;
      const restNodes = changedNodes.filter((node) => node !== typingNode);
      if (restNodes.length) api.refreshCells({ force: true, rowNodes: restNodes });
      if (typingNode) api.refreshCells({ force: true, rowNodes: [typingNode], columns: otherColumnIds() });
      return;
    }
    if (!focusedColId) {
      api.refreshCells({ force: true });
      return;
    }
    const otherColumns = otherColumnIds();
    if (otherColumns.length) api.refreshCells({ force: true, columns: otherColumns });
    /* The focused column still repaints on every row except the one being typed in: a fill-handle
       drag lands values on the rows below the focused cell, in that same column, and those cells
       would otherwise keep showing their pre-fill value until focus moved elsewhere. */
    const otherRowNodes: IRowNode<TRow>[] = [];
    api.forEachNode?.((node: IRowNode<TRow>) => {
      if (node.rowIndex !== focusedRowIndex) otherRowNodes.push(node);
    });
    if (otherRowNodes.length) api.refreshCells({ force: true, columns: [focusedColId], rowNodes: otherRowNodes });
  }, [rowData, refreshChangedRowsOnly, stableResolveRowId]);

  /* The loading overlay is driven by the reactive `loading` grid option passed
     straight to AgGridReact below — the deprecated api.showLoadingOverlay() call
     it replaces silently no-ops when it runs before the grid UI is attached, so
     the overlay never appeared for the initial page load. */

  const applyFullWidthHScroll = useCallback(() => {
    const api = gridRef.current?.api;
    const wrapper = wrapperRef.current;
    if (!api || api.isDestroyed?.() || !wrapper) return;
    const container = wrapper.querySelector<HTMLElement>('.ag-body-horizontal-scroll-container');
    if (!container) return;
    const displayed = [...(api.getDisplayedLeftColumns?.() || []), ...(api.getDisplayedCenterColumns?.() || []), ...(api.getDisplayedRightColumns?.() || [])];
    const totalWidth = displayed.reduce((sum, column) => sum + (column.getActualWidth?.() || 0), 0);
    const targetWidth = `${totalWidth}px`;
    if (totalWidth > 0 && container.style.width !== targetWidth) {
      container.style.width = targetWidth;
      /* Restoring the scroll position is not cosmetic — it is the other half of this override.
         AG Grid's own CenterWidthFeature sizes this container to the CENTRE columns alone and
         re-applies that on displayedColumnsChanged / columnContainerWidthChanged / the pinned-width
         events. The spacers are zeroed in CSS here, so the scrollbar viewport spans the whole grid
         and the container has to cover the pinned widths too — which means the two sizes disagree
         and AG Grid periodically shrinks the container back. Each shrink makes the browser clamp
         `scrollLeft` to the smaller maximum and AG Grid mirrors that clamp onto the body, so an
         edit or a column drag walked the grid leftwards. Re-applying the width without putting the
         position back is what made the scroll appear to jump. */
      const scrollViewport = wrapper.querySelector<HTMLElement>('.ag-body-horizontal-scroll-viewport');
      const preserved = lastHScrollLeftRef.current;
      if (scrollViewport && preserved > 0 && scrollViewport.scrollLeft !== preserved) scrollViewport.scrollLeft = preserved;
    }

    if (fullWidthHorizontalScrollEdgeRight) return;
    const pinnedRightWidth = (api.getDisplayedRightColumns?.() || []).reduce((sum, column) => sum + (column.getActualWidth?.() || 0), 0);
    const rightSpacer = wrapper.querySelector<HTMLElement>('.ag-horizontal-right-spacer');
    if (rightSpacer && pinnedRightWidth > 0) {
      rightSpacer.style.setProperty('width', `${pinnedRightWidth}px`, 'important');
      rightSpacer.style.setProperty('min-width', `${pinnedRightWidth}px`, 'important');
      rightSpacer.style.setProperty('max-width', `${pinnedRightWidth}px`, 'important');
      rightSpacer.style.setProperty('overflow', 'hidden', 'important');
    }
  }, [fullWidthHorizontalScrollEdgeRight]);

  const hasHeaderStats = Boolean(headerStats && Object.keys(headerStats).length > 0);
  const [statChipLayout, setStatChipLayout] = useState<StatChipPlacement[]>([]);

  /**
   * Measures where each stat chip should sit in the band above the grid.
   *
   * The band is rendered OUTSIDE the AG Grid root because AG Grid clips at `.ag-root` /
   * `.ag-root-wrapper`, both of which start at the header's top edge — a chip painted above them is
   * laid out but never drawn. Sitting outside means the chip cannot inherit the column's position,
   * so it is measured here and re-measured on every event that can move a column: reorder, resize,
   * horizontal scroll, virtualisation and grid resize.
   *
   * Pinned-left columns are already in viewport coordinates; centre columns are offset by the
   * pinned width and the current horizontal scroll.
   *
   * A centre chip is kept only while it fits ENTIRELY between the pinned blocks — the scrollable
   * region runs from the pinned-left edge to the pinned-right edge, so both are subtracted. The
   * test is on the chip's own extent, not the column's: the band clips at `overflow: hidden`, so a
   * chip anchored to a half-visible column used to be sliced mid-figure (`131970` reading as `13`)
   * or slid under the pinned Status column. A grid with no pinned columns has both widths at zero
   * and the region is the full viewport, which is the original behaviour unchanged.
   */
  const measureStatChips = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api || !headerStats || Object.keys(headerStats).length === 0) {
      setStatChipLayout((prev) => (prev.length ? [] : prev));
      return;
    }
    const columns = api.getAllDisplayedColumns?.() ?? [];
    const widthOfPinned = (side: 'left' | 'right'): number =>
      columns.filter((column) => column.getPinned() === side).reduce((total, column) => total + column.getActualWidth(), 0);
    const pinnedLeftWidth = widthOfPinned('left');
    const pinnedRightWidth = widthOfPinned('right');
    const scrolled = api.getHorizontalPixelRange?.();
    const scrollLeft = scrolled?.left ?? 0;
    const scrollableRightEdge = (wrapperRef.current?.clientWidth ?? 0) - pinnedRightWidth;

    const next: StatChipPlacement[] = [];
    columns.forEach((column) => {
      const stat = headerStats[column.getColId()];
      /* A zero or absent figure produces no slot at all, so the column keeps a plain header. */
      if (!stat || isBlankStat(stat.value)) return;
      const pinned = column.getPinned();
      if (pinned === 'right') return;
      const left = pinned === 'left' ? column.getLeft() ?? 0 : pinnedLeftWidth + ((column.getLeft() ?? 0) - scrollLeft);
      /* The chip sits `STAT_CHIP_INSET` into the column and is `STAT_CHIP_WIDTH` wide; both ends of
         that span must clear the pinned blocks or the band's overflow slices it. */
      if (pinned !== 'left' && (left + STAT_CHIP_INSET < pinnedLeftWidth || left + STAT_CHIP_INSET + STAT_CHIP_WIDTH > scrollableRightEdge)) return;
      next.push({ colId: column.getColId(), left, stat });
    });

    /* Bail out when nothing moved: this runs on every scroll frame, and returning a fresh array
       each time would re-render the band continuously. */
    setStatChipLayout((prev) => {
      const unchanged =
        prev.length === next.length &&
        prev.every((entry, index) => {
          const candidate = next[index];
          return (
            candidate !== undefined &&
            entry.colId === candidate.colId &&
            Math.round(entry.left) === Math.round(candidate.left) &&
            entry.stat.value === candidate.stat.value
          );
        });
      return unchanged ? prev : next;
    });
  }, [headerStats]);

  useEffect(() => {
    if (!hasHeaderStats) return undefined;
    measureStatChips();
    const raf = requestAnimationFrame(measureStatChips);
    return () => cancelAnimationFrame(raf);
  }, [hasHeaderStats, measureStatChips, columnDefs, rowData]);

  const mergedGridOptions = useMemo<GridOptions<TRow>>(() => {
    /*
     * A caller-supplied defaultColDef is spread onto AgGridReact AFTER the grid's own
     * `defaultColDef` prop, so passing one used to REPLACE the baseline outright — silently
     * dropping SortableHeader and, more damagingly, the suppressKeyboardEvent that hands key
     * presses inside an INPUT to the input rather than the grid. Without it Ctrl+A inside a cell
     * selects every cell in the grid instead of the text being typed. Merge so a caller extends
     * the baseline instead of replacing it.
     */
    const withDefaults: GridOptions<TRow> = gridOptions.defaultColDef
      ? { ...gridOptions, defaultColDef: { ...defaultColDef, ...gridOptions.defaultColDef } }
      : gridOptions;
    if (!onLoadMore && !enableFillHandle && !enableCellCopy && !fullWidthHorizontalScroll && !enableActiveCellNavigation && !enableRowDrag) return withDefaults;
    const merged: GridOptions<TRow> = { ...withDefaults };

    if (enableActiveCellNavigation) {
      /**
       * All four arrows land only on cells that are editable for that row: Left/Right walk the
       * columns of the current row, Up/Down walk the rows of the current column. Pinned (footer)
       * rows never navigate. Overrides any caller-supplied navigateToNextCell while the feature
       * is on.
       */
      merged.navigateToNextCell = (params) => {
        const previous = params.previousCellPosition;
        if (previous.rowPinned) return null;
        const { leafDefs, gridReadOnly } = activeCellNavRef.current;
        const { isRowReadOnly } = handlersRef.current;
        const readRow = (rowIndex: number) => params.api.getDisplayedRowAtIndex?.(rowIndex)?.data as TRow | undefined;

        if (params.key === 'ArrowUp' || params.key === 'ArrowDown') {
          const rowStep = params.key === 'ArrowDown' ? 1 : -1;
          const nextRowIndex = findNextNavigableRowIndex(
            params.api.getDisplayedRowCount?.() ?? 0,
            previous.rowIndex,
            rowStep,
            leafDefs.get(previous.column.getColId()),
            readRow,
            gridReadOnly,
            isRowReadOnly,
          );
          return nextRowIndex === null ? null : { rowIndex: nextRowIndex, rowPinned: previous.rowPinned, column: previous.column };
        }

        if (params.key !== 'ArrowLeft' && params.key !== 'ArrowRight') return null;
        const direction = params.key === 'ArrowRight' ? 1 : -1;
        const displayed = params.api.getAllDisplayedColumns?.() ?? [];
        const orderedColIds = displayed.map((column) => column.getColId());
        const nextColId = findNextNavigableColId(
          orderedColIds,
          previous.column.getColId(),
          direction,
          (colId) => leafDefs.get(colId),
          readRow(previous.rowIndex),
          gridReadOnly,
          isRowReadOnly,
        );
        if (!nextColId) return null;
        const nextColumn = displayed.find((column) => column.getColId() === nextColId);
        return nextColumn ? { rowIndex: previous.rowIndex, rowPinned: previous.rowPinned, column: nextColumn } : null;
      };
    }

    if (onLoadMore) {
      merged.onBodyScrollEnd = (params) => {
        gridOptions.onBodyScrollEnd?.(params);
        if (!hasMore || loadingMore) return;
        const range = params.api.getVerticalPixelRange();
        /* Must use this grid's actual row height: assuming the 40px default makes
           the content look shorter than it is, firing load-more near the top. */
        const effectiveRowHeight = gridOptions.rowHeight ?? DEFAULT_ROW_HEIGHT;
        const bottom = params.api.getDisplayedRowCount() * effectiveRowHeight;
        if (range && range.bottom >= bottom - LOAD_MORE_THRESHOLD_PX) onLoadMore();
      };
    }

    if (enableFillHandle) {
      merged.cellSelection = gridOptions.cellSelection ?? { handle: { mode: 'fill', direction: fillHandleDirection, setFillValue: copyFillValue } };
      merged.suppressClickEdit = gridOptions.suppressClickEdit ?? true;
      merged.onCellValueChanged = (event) => {
        gridOptions.onCellValueChanged?.(event);
        handleFillValueChange(event);
      };
      merged.onFillEnd = (event) => {
        gridOptions.onFillEnd?.(event);
        recordFillSource(event);
      };
    }

    if (enableCellCopy && !enableFillHandle) {
      merged.cellSelection = gridOptions.cellSelection ?? true;
      merged.suppressClipboardPaste = gridOptions.suppressClipboardPaste ?? true;
    }

    if (enableFillHandle || enableCellCopy) {
      merged.processCellForClipboard = gridOptions.processCellForClipboard ?? ((params) => clipboardTextOf(params, activeCellNavRef.current.leafDefs));
      merged.processCellFromClipboard = gridOptions.processCellFromClipboard ?? ((params) => clipboardValueOf(params, activeCellNavRef.current.leafDefs));
    }

    if (fullWidthHorizontalScroll) {
      const chain =
        <T,>(userHandler: ((event: T) => void) | undefined) =>
        (event: T) => {
          userHandler?.(event);
          applyFullWidthHScroll();
        };
      merged.onFirstDataRendered = chain(gridOptions.onFirstDataRendered);
      merged.onDisplayedColumnsChanged = chain(gridOptions.onDisplayedColumnsChanged);
      merged.onColumnResized = chain(gridOptions.onColumnResized);
      merged.onGridSizeChanged = chain(gridOptions.onGridSizeChanged);
      merged.onVirtualColumnsChanged = chain(gridOptions.onVirtualColumnsChanged);
      /* Records where the user actually is. `direction` distinguishes a real horizontal scroll from
         the vertical ones AG Grid also reports here, so a clamped value written during the width
         churn is never mistaken for an intentional move. */
      const userScroll = gridOptions.onBodyScroll;
      merged.onBodyScroll = (event) => {
        userScroll?.(event);
        if (event?.direction === 'horizontal') lastHScrollLeftRef.current = Math.max(0, event.left ?? 0);
      };
    }

    /* The stat band sits outside the grid, so it cannot inherit column geometry — it has to be
       re-measured on everything that moves a column. `onColumnMoved` is included because a drag
       repositions columns continuously, and `onBodyScroll` because the band must track horizontal
       scrolling frame by frame. Chained after any caller handler so nothing is swallowed. */
    if (hasHeaderStats) {
      const track =
        <T,>(userHandler: ((event: T) => void) | undefined) =>
        (event: T) => {
          userHandler?.(event);
          measureStatChips();
        };
      merged.onFirstDataRendered = track(merged.onFirstDataRendered ?? gridOptions.onFirstDataRendered);
      merged.onDisplayedColumnsChanged = track(merged.onDisplayedColumnsChanged ?? gridOptions.onDisplayedColumnsChanged);
      merged.onColumnResized = track(merged.onColumnResized ?? gridOptions.onColumnResized);
      merged.onGridSizeChanged = track(merged.onGridSizeChanged ?? gridOptions.onGridSizeChanged);
      merged.onVirtualColumnsChanged = track(merged.onVirtualColumnsChanged ?? gridOptions.onVirtualColumnsChanged);
      merged.onColumnMoved = track(gridOptions.onColumnMoved);
      merged.onBodyScroll = track(merged.onBodyScroll ?? gridOptions.onBodyScroll);
    }

    if (enableRowDrag) {
      merged.rowDragManaged = true;
      /* Read the order back off the grid rather than tracking indices through the drag: AG Grid has
         already applied the move by the time this fires, so `forEachNode` is the truth and draft
         rows need no special handling. A caller-supplied onRowDragEnd still runs. */
      merged.onRowDragEnd = (event) => {
        gridOptions.onRowDragEnd?.(event);
        if (!onRowReorder) return;
        const ordered: TRow[] = [];
        event.api.forEachNode((node) => {
          if (node.data) ordered.push(node.data);
        });
        onRowReorder(ordered);
      };
    }

    return merged;
  }, [
    enableRowDrag,
    onRowReorder,
    onLoadMore,
    hasMore,
    loadingMore,
    gridOptions,
    enableFillHandle,
    fillHandleDirection,
    handleFillValueChange,
    fullWidthHorizontalScroll,
    applyFullWidthHScroll,
    enableActiveCellNavigation,
    defaultColDef,
    enableCellCopy,
    recordFillSource,
  ]);

  const useAutoHeight = !height;
  /* True when the injected 36px drag handle is the only column pinned left — no expand chevron and
     no caller column claims that side. Drives the seam suppression below. */
  const dragOnlyPinnedLeft = useMemo(() => {
    if (!enableRowDrag) return false;
    if (detailRowRenderer && !treeData) return false;
    const isPinnedLeft = (col: BombiixColumnDefOrGroup<TRow>): boolean => {
      const group = col as BombiixColumnGroupDef<TRow>;
      if (Array.isArray(group.children)) return group.children.some(isPinnedLeft);
      const { pinned } = col as BombiixColumnDef<TRow>;
      return pinned === 'left' || pinned === true;
    };
    return !(columnDefs ?? []).some(isPinnedLeft);
  }, [enableRowDrag, detailRowRenderer, treeData, columnDefs]);
  const wrapperClass = [
    'cag-wrapper',
    useAutoHeight ? 'cag-wrapper--auto' : '',
    revealHeaderControlsOnHover ? 'cag-wrapper--hover-controls' : '',
    suppressColumnResize ? 'cag-wrapper--no-resize' : '',
    fullWidthHorizontalScroll ? 'cag-wrapper--full-hscroll' : '',
    hideCellErrorMessage ? 'cag-wrapper--no-cell-error' : '',
    enableActiveCellNavigation ? 'cag-wrapper--active-cell-nav' : '',
    /* Unclips the header so stat chips can sit above it. Only applied when the caller actually
       supplies chips — every other grid keeps AG Grid's default header clipping. */
    hasHeaderStats ? 'cag-wrapper--header-stats' : '',
    /* The pinned↔centre seam marks where frozen columns end. When the injected drag handle is the
       ONLY thing pinned left, there is nothing frozen to mark and the seam reads as a stray vertical
       rule down the left edge of the table. Suppressed in that case only — a grid that also pins a
       real column keeps the seam, because there the boundary is meaningful. */
    dragOnlyPinnedLeft ? 'cag-wrapper--drag-only-pinned-left' : '',
  ]
    .filter(Boolean)
    .join(' ');
  /* Dynamic, caller-supplied height is the documented exception to the
     no-inline-style rule. */
  const themeStyle = useAutoHeight ? undefined : { height };

  const getMainMenuItems: GetMainMenuItems<TRow> = useCallback((params) => params.defaultItems.filter((item) => item !== 'columnChooser'), []);

  const isRowMasterResolved = !treeData ? isRowMaster || (detailRowRenderer ? () => true : undefined) : undefined;

  const gridExtraProps: Partial<React.ComponentProps<typeof AgGridReact<TRow>>> = {};
  if (pinnedBottomRowData) gridExtraProps.pinnedBottomRowData = pinnedBottomRowData;
  if (detailRowRenderer && !treeData) gridExtraProps.detailCellRenderer = detailRowRenderer;
  if (isRowMasterResolved) gridExtraProps.isRowMaster = isRowMasterResolved as (row: TRow) => boolean;
  if (treeData && getDataPath) gridExtraProps.getDataPath = getDataPath as (row: TRow) => string[];
  if (resolvedAutoGroupColumnDef) gridExtraProps.autoGroupColumnDef = resolvedAutoGroupColumnDef;
  if (treeData) gridExtraProps.groupDefaultExpanded = groupDefaultExpanded;
  if (useAutoHeight) gridExtraProps.domLayout = 'autoHeight';
  if (!showColumnChooser) gridExtraProps.getMainMenuItems = getMainMenuItems;

  return (
    <div className={wrapperClass} ref={wrapperRef}>
      {/* Aggregate chips, floating above the table rather than inside the header row. Rendered here,
          outside the AG Grid root, because AG Grid clips anything drawn above `.ag-root`. */}
      {hasHeaderStats ? (
        <div className="cag-header-stat-band" aria-hidden="true">
          {statChipLayout.map((placement) => (
            <span key={placement.colId} className="cag-header-stat-slot" style={{ left: `${placement.left}px` }}>
              <HeaderStatChip {...placement.stat} />
            </span>
          ))}
        </div>
      ) : null}
      <div className="ag-theme-alpine" style={themeStyle}>
        <AgGridReact<TRow>
          ref={gridRef}
          theme="legacy"
          columnDefs={builtColumnDefs}
          rowData={rowData}
          defaultColDef={defaultColDef}
          getRowId={getGridRowId}
          getRowClass={composedRowClass}
          masterDetail={Boolean(detailRowRenderer) && !treeData}
          treeData={treeData}
          headerHeight={HEADER_HEIGHT}
          rowHeight={40}
          animateRows={false}
          suppressAnimationFrame
          suppressColumnVirtualisation
          suppressScrollOnNewData
          rowBuffer={10}
          loading={loading}
          loadingOverlayComponent={Loader}
          suppressDragLeaveHidesColumns
          suppressCellFocus={!enableFillHandle && !enableActiveCellNavigation && !enableCellCopy}
          suppressNoRowsOverlay={loading}
          tooltipShowMode="whenTruncated"
          tooltipShowDelay={GRID_TOOLTIP_SHOW_DELAY_MS}
          suppressContextMenu={!enableClipboardContextMenu}
          {...(enableClipboardContextMenu ? { getContextMenuItems: getClipboardContextMenuItems } : {})}
          {...gridExtraProps}
          {...mergedGridOptions}
        />
      </div>
      <FloatingBottomBar actions={floatingActions} />
    </div>
  );
}

export default BombiixAGGrid;
export { BombiixAGGrid };
